import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import SavingsRoundedIcon from '@mui/icons-material/SavingsRounded';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import { Link as RouterLink, useNavigate } from 'react-router';
import { registerCustomer } from '../../services/authService';
import './Auth.scss';

const signUpBenefits = [
  {
    icon: <Inventory2RoundedIcon fontSize='small' />,
    title: 'Quick reorders',
    text: 'Create your account once and reorder popular items in just a few taps.',
  },
  {
    icon: <SavingsRoundedIcon fontSize='small' />,
    title: 'Better offers',
    text: 'Unlock account-based promotions, coupons, and personalized savings.',
  },
  {
    icon: <FavoriteBorderRoundedIcon fontSize='small' />,
    title: 'Keep your picks',
    text: 'Save favorites and build a more convenient grocery and snack routine.',
  },
];

export default function SignUp() {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
  });

  const handleFieldChange = (field) => (event) => {
    const value = event.target.value;

    setFormValues((current) => ({
      ...current,
      [field]: value,
    }));

    setFieldErrors((current) => ({
      ...current,
      [field]: '',
    }));

    setSubmitError(null);
  };

  const validateForm = () => {
    const errors = {};

    if (!formValues.name.trim()) {
      errors.name = 'Please enter your full name.';
    }

    if (!formValues.email.trim()) {
      errors.email = 'Please enter your email address.';
    } else if (!/^\S+@\S+\.\S+$/.test(formValues.email.trim())) {
      errors.email = 'Please enter a valid email address.';
    }

    if (!formValues.mobile.trim()) {
      errors.mobile = 'Please enter your mobile number.';
    } else if (!/^\d{10}$/.test(formValues.mobile.trim())) {
      errors.mobile = 'Mobile number must be 10 digits.';
    }

    if (!formValues.password) {
      errors.password = 'Please create a password.';
    } else if (formValues.password.length < 6) {
      errors.password = 'Password must be at least 6 characters.';
    }

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validateForm();

    if (Object.keys(validationErrors).length > 0) {
      setFieldErrors(validationErrors);
      setSubmitError({
        message: 'Please correct the highlighted fields before continuing.',
      });
      setSubmitSuccess('');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess('');
    setFieldErrors({});

    try {
      const data = await registerCustomer(formValues);
      setSubmitSuccess(
        data?.message || 'Your account was created successfully. Please sign in.'
      );
      setFormValues({
        name: '',
        email: '',
        mobile: '',
        password: '',
      });

      window.setTimeout(() => {
        navigate('/signin');
      }, 1200);
    } catch (error) {
      setSubmitError(error);
      setFieldErrors(error.validationErrors || {});
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className='auth-page'>
      <Box className='auth-shell'>
        <Box className='auth-panel auth-panel--brand'>
          <Chip
            className='auth-chip'
            icon={<PersonAddAltRoundedIcon />}
            label='Customer Sign Up'
          />

          <Typography className='auth-kicker'>Fresh account setup</Typography>
          <Typography variant='h2' className='auth-title'>
            Create your account and make every future order smoother.
          </Typography>
          <Typography className='auth-subtitle'>
            Join with your basic details to save addresses, manage orders,
            discover better offers, and check out faster next time.
          </Typography>

          <Stack spacing={2.25} className='auth-benefits'>
            {signUpBenefits.map((item) => (
              <Box key={item.title} className='auth-benefit'>
                <Box className='auth-benefitIcon'>{item.icon}</Box>
                <Box>
                  <Typography className='auth-benefitTitle'>
                    {item.title}
                  </Typography>
                  <Typography className='auth-benefitText'>
                    {item.text}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Box>

        <Box className='auth-panel auth-panel--form'>
          <Box className='auth-formWrap'>
            <Typography variant='h4' className='auth-formTitle'>
              Create account
            </Typography>
            <Typography className='auth-formSubtitle'>
              Enter your name, email, mobile number, and password to sign up.
            </Typography>

            <Alert severity='info' className='auth-alert'>
              Your account helps us save orders, addresses, and coupons for you.
            </Alert>

            {submitError ? (
              <Alert severity='error' className='auth-alert'>
                <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                  Sign up failed
                </Typography>
                <Typography>{submitError.message}</Typography>
                {Object.keys(fieldErrors).length > 0 ? (
                  <Box component='ul' className='auth-errorList'>
                    {Object.entries(fieldErrors).map(([field, message]) => (
                      <li key={field}>{message}</li>
                    ))}
                  </Box>
                ) : null}
              </Alert>
            ) : null}

            {submitSuccess ? (
              <Alert severity='success' className='auth-alert'>
                {submitSuccess}
              </Alert>
            ) : null}

            <Stack
              component='form'
              spacing={2.25}
              className='auth-form'
              onSubmit={handleSubmit}
            >
              <TextField
                fullWidth
                label='Full name'
                placeholder='Enter your name'
                value={formValues.name}
                onChange={handleFieldChange('name')}
                error={Boolean(fieldErrors.name)}
                helperText={fieldErrors.name}
              />
              <TextField
                fullWidth
                label='Email'
                type='email'
                placeholder='name@email.com'
                value={formValues.email}
                onChange={handleFieldChange('email')}
                error={Boolean(fieldErrors.email)}
                helperText={fieldErrors.email}
              />
              <TextField
                fullWidth
                label='Mobile'
                placeholder='9876543210'
                value={formValues.mobile}
                onChange={handleFieldChange('mobile')}
                error={Boolean(fieldErrors.mobile)}
                helperText={fieldErrors.mobile}
              />
              <TextField
                fullWidth
                label='Password'
                type={showPassword ? 'text' : 'password'}
                placeholder='Create a password'
                value={formValues.password}
                onChange={handleFieldChange('password')}
                error={Boolean(fieldErrors.password)}
                helperText={fieldErrors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        edge='end'
                        onClick={() => setShowPassword((value) => !value)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <VisibilityOffRoundedIcon />
                        ) : (
                          <VisibilityRoundedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box className='auth-inlineRow'>
                <Link
                  component={RouterLink}
                  to='/signin'
                  underline='hover'
                  className='auth-link'
                >
                  Already have an account? Sign in
                </Link>
              </Box>

              <Button
                type='submit'
                variant='contained'
                size='large'
                disableElevation
                className='auth-submit'
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating account...' : 'Sign up'}
              </Button>
            </Stack>

            <Divider className='auth-divider'>Or continue with</Divider>

            <Stack className='auth-socials'>
              <Button variant='outlined' className='auth-socialButton'>
                <Box className='auth-socialMark auth-socialMark--google'>G</Box>
                Continue with Google
              </Button>
              <Button variant='outlined' className='auth-socialButton'>
                <Box className='auth-socialMark auth-socialMark--facebook'>f</Box>
                Continue with Facebook
              </Button>
              <Button variant='outlined' className='auth-socialButton'>
                <Box className='auth-socialMark auth-socialMark--apple'>A</Box>
                Continue with Apple
              </Button>
            </Stack>

            <Typography className='auth-footerText'>
              By signing up, you can keep your favorites, manage delivery
              details, and enjoy a more personalized shopping experience.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
